// The desk shell: a keyboard, a mouse, and registers that are read by scanning.
//
// The counterpart to OperatorShell, and deliberately a different product rather
// than the same layout at a different width — see the note at the top of
// OperatorShell.tsx for the failure that ruling came out of. What the office
// needs and the line must never get: a persistent sidebar, dense tables, small
// (but never smaller than 44px) targets, and every register visible at once.
//
// Two things in here are not decoration:
//   - `useResource` is where every office page loads and reports failure. It
//     lives in the shell so the verbatim-server-message rule below is enforced in
//     one place instead of being remembered in six pages.
//   - NavLink sets `aria-current="page"` on the active item by itself. Do not
//     replace it with a plain <a> and a className — the current item would then be
//     visible only to people who can see the highlight.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Activity,
  ClipboardList,
  Gauge,
  GraduationCap,
  Menu,
  PackageCheck,
  PauseOctagon,
  ClipboardCheck,
  Truck,
  TriangleAlert,
  ShieldCheck,
  MessageSquareWarning,
  type LucideIcon,
} from 'lucide-react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ThemeSwitch } from '@/components/ThemeSwitch';
import { currentUpn } from '../config/auth';
import { api } from '../lib/api';
import { useDocumentTitle, usePageName } from '../lib/pageTitle';
import { actionLabel } from '../lib/vocabulary';
import { handOverState, useQueue, type HandOverState } from '../offline/useQueue';
import { useIirQueue } from '../offline/useIirQueue';
import { localDay, type InspectionFailuresPayload, type RegisterPayload } from '../pages/office/goodsInModel';

/**
 * Focus ring for every control in the office shell.
 *
 * ONE RING, and it is the global one: `*:focus-visible` in index.css draws a 2px outline
 * in `--focus`, offset 2px, on everything. Until the UI/UX audit (27 Oct 2026, item 25)
 * this constant hid that outline and drew a second, different ring in `--primary`, so a
 * tab stop looked one way on a form field and another on a plain link. It is kept as an
 * export because forty call sites append it; it adds nothing, on purpose. Do not put a
 * `ring-*` or `outline-hidden` class back in here - the sidebar's darker ground gets its
 * ring by re-pointing the `--focus` token (index.css), never by a second style.
 */
export const officeFocus = '';

/** Nothing in this shell is smaller than this, including table row controls. */
export const rowControl =
  'inline-flex min-h-[44px] items-center justify-center rounded-(--radius-sm) px-(--s3) ' +
  'text-(length:--fs-sm) font-semibold';

/**
 * The sentence for a request that never left the browser because no access token
 * could be issued: `request` in lib/api.ts files that as status 401 with reason
 * `token_unavailable`, carrying the sentence auth.ts wrote. Checked by shape, like
 * the rest of this file, so a test that stubs lib/api need not stub this too.
 */
export function tokenUnavailableMessage(err: unknown): string | null {
  if (!(err instanceof Error) || !err.message) return null;
  return (err as { reason?: unknown }).reason === 'token_unavailable' ? err.message : null;
}

/**
 * The message a failed call actually carried.
 *
 * ApiError's own `message` is "API 403 Forbidden: /api/specs/12/approve". The
 * sentence a person needs is in the response body, which this API always shapes
 * as `{ error: "<sentence>" }` — and for a refused approval that sentence is the
 * reason, written by the gate that refused it. Showing a generic "something went
 * wrong" in its place throws away the only part of the response worth reading.
 *
 * There is no friendly fallback on purpose. If the server sent no sentence, the
 * status code is what gets shown, because somebody can look that up.
 */
export function serverMessage(err: unknown): string {
  if (err && typeof err === 'object') {
    const body = (err as { body?: unknown }).body;
    if (body && typeof body === 'object' && 'error' in body) {
      const sentence = (body as { error?: unknown }).error;
      if (typeof sentence === 'string' && sentence.trim()) return sentence;
    }
    // A token that could not be issued means the request never left the browser.
    // api.ts files it as a 401 (reason `token_unavailable`) so callers have one
    // type to catch, but "the server refused this" would be false — and the
    // sentence auth.ts wrote says what to do.
    const signIn = tokenUnavailableMessage(err);
    if (signIn) return signIn;
    const status = (err as { status?: unknown }).status;
    const statusText = (err as { statusText?: unknown }).statusText;
    if (typeof status === 'number') {
      return `The server refused this (${status}${
        typeof statusText === 'string' && statusText ? ` ${statusText}` : ''
      }) and sent no message.`;
    }
  }
  if (err instanceof Error && err.message && !err.message.startsWith('API ')) return err.message;
  return 'The API could not be reached.';
}

export interface Resource<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Load one GET endpoint, with the server's own words on failure.
 *
 * Pass `null` as the path to hold off (a detail pane with nothing selected).
 * `reload` is what every write on an office page calls once the write returns —
 * the registers here are the record, so the screen re-reads rather than patching
 * its own copy and hoping the two agree.
 */
/** True below Tailwind's `sm` (640 px): the phone layouts switch on it. Follows the window as it changes. */
export function usePhone(): boolean {
  const query = '(max-width: 639px)';
  const [phone, setPhone] = useState(() => (typeof window !== 'undefined' && 'matchMedia' in window ? window.matchMedia(query).matches : false));
  useEffect(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) return;
    const mq = window.matchMedia(query);
    const on = () => setPhone(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return phone;
}

export function useResource<T>(path: string | null): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  // True from the first render when there is a path: the table must not draw its empty sentence
  // for the frame before the effect runs (rule 15; UI/UX audit, 27 Oct 2026).
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!path) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(path)
      .then((d: unknown) => {
        if (!cancelled) setData(d as T);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(serverMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload };
}

/** A list endpoint returns a bare array. Guard anyway: one unexpected envelope
 *  should cost a page an empty table, not a white screen. */
export function rows<T>(data: unknown): T[] {
  return Array.isArray(data) ? (data as T[]) : [];
}

/**
 * The instant an `<input type="date">` value means HERE, as ISO.
 *
 * ⚠ Send this, never the raw "2026-09-16". A bare date string is parsed as UTC
 * midnight, and Brisbane is UTC+10: for the first ten hours of every working day,
 * "today" resolves to an instant that has not happened yet. The API refuses a
 * future `assessedAt` outright, and a grant posted the same way does not come
 * into force until 10am — the QA manager grants CAPTURE at eight and the line is
 * refused until mid-morning, with nothing on either screen saying why.
 *
 * Returns null for an empty or unparseable value, which every caller sends as
 * null rather than as a date the server would have to guess at.
 */
export function localInstant(dateInput: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateInput.trim());
  if (!match) return null;
  const [, y, m, d] = match;
  // Month is 0-based; this constructor is local time, which is the whole point.
  const at = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/**
 * The day an instant falls on HERE, as YYYY-MM-DD — `localInstant` read back.
 *
 * ⚠ Not `toISOString().slice(0, 10)`: that is the UTC day, and local midnight in
 * Brisbane is 14:00 the day before in UTC. Every date the office saves is a local
 * midnight, so every one of them read a day early: a grant from 1 Oct showed
 * "2026-09-30" (2026-10-01). Null for a value that is not an instant.
 */
export function localDate(at: string | number | Date): string | null {
  const d = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/* ── Subject keys ─────────────────────────────────────────
 * A specification is keyed on a SUBJECT and resolved AT A MOMENT
 * (`GET /specs?subjectType=&subjectId=&at=`). There is no route that reads a
 * version by its own id and there cannot usefully be one — which version is in
 * force is a question about time, not about a primary key. So the spec pages
 * address a subject, and these two functions are the encoding they share.
 */
export type SubjectType = 'PRODUCT' | 'LINE' | 'AREA' | 'ASSET' | 'SUPPLIER' | 'MATERIAL';

export const SUBJECT_TYPES: SubjectType[] = ['PRODUCT', 'LINE', 'AREA', 'ASSET', 'SUPPLIER', 'MATERIAL'];

export function subjectKey(type: SubjectType, id: number): string {
  return `${type}-${id}`;
}

export function parseSubjectKey(key: string | undefined): { type: SubjectType; id: number } | null {
  if (!key) return null;
  const [type, id] = key.split('-');
  const parsed = Number(id);
  if (!SUBJECT_TYPES.includes(type as SubjectType)) return null;
  if (!Number.isInteger(parsed) || parsed <= 0) return null;
  return { type: type as SubjectType, id: parsed };
}

/* Tables: every register renders through components/DataTable.tsx. */

/** A form field and its label, for the office's hand-built forms (spec master,
 *  the draft editor). One definition, so two forms cannot drift apart. */
export const fieldClass =
  'flex min-h-[44px] rounded-md border border-hairline-strong bg-card px-3 text-sm ' +
  'transition-[border-color,box-shadow] duration-150 motion-reduce:transition-none';
export const labelClass = 'text-[13px] font-semibold text-muted-foreground';

/* ── Authority ────────────────────────────────────────────
 * Every action a grant in dbo.Authorisation can carry.
 */
export type AuthAction =
  | 'CAPTURE'
  | 'SPEC_APPROVE'
  | 'RELEASE'
  | 'CONCESSION'
  | 'ASSESS'
  | 'METER_ADMIN'
  | 'AUTH_ADMIN'
  // VERIFY (slice 1b): may sign off somebody else's start-up check. Never their
  // own — the server refuses self-verification whatever grants are held.
  | 'VERIFY'
  // ARTWORK_SIGNOFF (migration 011): may ACCEPT or REJECT a draft artwork. Used
  // through chat only — artwork has no screen in this app — but granted here.
  | 'ARTWORK_SIGNOFF'
  // RECEIVE (migration 017): may record an incoming inspection (IIR) at the dock,
  // on the operator shell's Receive screen.
  | 'RECEIVE'
  // COMPLAINT_TRIAGE (migration 042): consumer care on the Complaints page. Severity and the
  // finding stay with RELEASE.
  | 'COMPLAINT_TRIAGE'
  // Migration 053: the hold and release deciders (R&D, Manufacturing Manager, Logistics Manager).
  | 'DISPOSITION_RD'
  | 'DISPOSITION_MFG'
  | 'DISPOSITION_LOGISTICS';

export interface MyGrants {
  holds: (action: AuthAction) => boolean;
  /** True once the register has actually answered. False while loading, on
   *  failure, and with nobody signed in — three different things; `error` and
   *  `signedIn` tell them apart. */
  known: boolean;
  /** Somebody is signed in, so the register is (being) asked. */
  signedIn: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * What the signed-in person is authorised to do.
 *
 * This is a READ OF THE REGISTER, not a token claim. A directory role or an app
 * role says something about a job; `dbo.Authorisation` says who granted what,
 * from when, until when — which is the thing an auditor asks to see, and the only
 * thing this app treats as authority. So the SPA asks the API which grants the
 * caller holds rather than decoding its own token.
 *
 * It fails closed in both directions. A control is disabled until the register
 * says the grant is held, including while the lookup is still in flight or after
 * it failed; and the server checks again regardless, because a button that was
 * merely hidden is not a control.
 */
export function useMyGrants(): MyGrants {
  // ⚠ ONE FUNCTION ANSWERS "who is signed in", and it is currentUpn(). This used
  // to gate on `accounts.length > 0` as well, and the two could disagree: under
  // AUTH_BYPASS there is no MSAL account but there IS a UPN, so the register was
  // never asked for, `known` stayed false for ever, and anything waiting on it
  // waited for ever. `accounts` is still subscribed to because it is what makes
  // this component re-render when the signed-in account changes; it is no longer
  // what decides.
  useMsal();
  const upn = currentUpn();

  // TWO FILTERS, AND BOTH ARE THE POINT. `upn` because the register lists
  // everybody — asking for the whole table and answering "somebody holds
  // SPEC_APPROVE" would enable the button for a person who holds nothing.
  // `activeOnly=true` because a grant has dates, and a lapsed row is not
  // authority — the server's holdsGrant applies exactly this window, so the
  // screen and the gate agree.
  //
  // With nobody signed in the path is null: nothing is fetched, `known` stays
  // false, and every control stays disabled. That is the right answer, not a
  // bug — no account, no authority.
  const { data, error, reload } = useResource<Array<{ action: AuthAction }>>(
    upn ? `/authorisations?upn=${encodeURIComponent(upn)}&activeOnly=true` : null,
  );
  const held = new Set(rows<{ action: AuthAction }>(data).map((g) => g.action));
  return {
    holds: (action) => held.has(action),
    known: data !== null && error === null,
    signedIn: upn !== null,
    error,
    reload,
  };
}

/** The sentence a disabled control shows, so "greyed out" is never the whole
 *  explanation. Before the register has answered there are three reasons, and
 *  only one is a failure (rule 15): until 2026-10-01 every page load said "could
 *  not be read" for the second it took to ask. Only a failure is recoverable by
 *  reloading, so only a failure says so. */
export function grantHint(grants: MyGrants, action: AuthAction, what: string): string | null {
  if (grants.holds(action)) return null;
  if (!grants.known) {
    if (grants.error !== null) {
      return `Your authorisations could not be read, so ${what} is disabled. Reload the register.`;
    }
    if (!grants.signedIn) return `Nobody is signed in, so ${what} is disabled.`;
    return 'Reading your authorisations…';
  }
  // In words, with the code kept for matching the register (UI/UX audit, 31 Oct 2026, UX-17):
  // "needs a current METER_ADMIN authorisation. Ask an AUTH_ADMIN" named two codes and no task.
  return `${what} needs a current ${actionLabel(action)} authorisation (${action}). Ask a QA manager to grant it.`;
}

/**
 * The grants that mean "this person's work is at a desk": Landing sends their holders to /office,
 * and the operator shell offers them the Office tab back. The same list in both places (audit 28 Oct
 * 2026: the tab used SPEC_APPROVE/AUTH_ADMIN only, so RELEASE-only QA had no way back).
 * ⚠ Landing.tsx keeps its own copy until it imports this one; shells/officeActions.test.ts pins
 * the two equal.
 */
export const OFFICE_ACTIONS: readonly AuthAction[] = ['SPEC_APPROVE', 'AUTH_ADMIN', 'ASSESS', 'METER_ADMIN', 'CONCESSION', 'RELEASE'];

/* ── A new build (audit 28 Oct 2026) ──────────────────────
 * A docked iPad left open keeps the bundle it loaded: navigations are network-first, but nothing
 * navigates. So the page asks for index.html now and then and compares the entry script it names
 * (Vite hashes it per build) with the one it is running. A new one shows a strip with Reload -
 * NEVER an automatic reload: a reading half-typed on the keypad is not in the queue yet.
 */
const BUILD_CHECK_MS = 10 * 60_000;

/** The module entry script a page names (`<script type="module" … src="/assets/index-<hash>.js">`). */
export function entryScript(html: string): string | null {
  for (const tag of html.match(/<script\b[^>]*>/gi) ?? []) {
    if (!/\btype=["']module["']/i.test(tag)) continue;
    const src = /\bsrc=["']([^"']+)["']/i.exec(tag);
    if (src) return src[1];
  }
  return null;
}

/** A different build is served. Unknown on either side is not "new" (rule 15: never claim it). */
export function isNewBuild(running: string | null, served: string | null): boolean {
  return running !== null && served !== null && running !== served;
}

export function useNewBuild(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (import.meta.env.DEV || typeof document === 'undefined') return;
    const running = entryScript(
      [...document.querySelectorAll('script[type="module"][src]')].map((el) => el.outerHTML).join(''),
    );
    if (!running) return;
    let done = false;
    const check = async () => {
      if (done || document.visibilityState === 'hidden') return;
      try {
        // A URL the service worker has never stored, and no-store: the build served NOW.
        const res = await fetch(`/index.html?build-check=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok || !(res.headers.get('content-type') ?? '').includes('text/html')) return;
        if (isNewBuild(running, entryScript(await res.text()))) {
          done = true;
          setReady(true);
        }
      } catch {
        // Offline: asked again on the next tick, or when the link returns.
      }
    };
    const onWake = () => void check();
    const timer = setInterval(onWake, BUILD_CHECK_MS);
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('online', onWake);
    navigator.serviceWorker?.addEventListener('controllerchange', onWake);
    return () => {
      done = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('online', onWake);
      navigator.serviceWorker?.removeEventListener('controllerchange', onWake);
    };
  }, []);
  return ready;
}

/** The strip both shells show once a new build is served. `large`: the operator shell's 56px target. */
export function NewBuildNotice({ large = false }: { large?: boolean }) {
  if (!useNewBuild()) return null;
  return (
    <div
      role="status"
      className="flex flex-none flex-wrap items-center gap-(--s3) border-b border-(--border) bg-(--card) px-(--s4) py-(--s2) text-(length:--fs-sm) text-(--fg)"
    >
      <span className="min-w-0 flex-1">
        A new version of Quality is ready. Reload between checks: anything waiting to send stays on this device.
      </span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className={`${large ? 'min-h-[56px]' : 'min-h-[44px]'} rounded-(--radius) border border-(--border-strong) bg-(--bg) px-(--s4) font-semibold`}
      >
        Reload
      </button>
    </div>
  );
}

/** Why Sign out is refused, or null when it is not: the operator shell's Hand over rule. */
export function signOutRefusal(state: HandOverState): string | null {
  if (state.canHandOver) return null;
  return state.depthKnown
    ? `${state.toSend} reading(s), inspection(s) or photo(s) are still to send or refused on this device. They are stamped with your name and cannot be sent by anyone else. Open Line check or Receiving to send or clear them, then sign out.`
    : 'This device cannot read its own queue, so we cannot tell whether anything is still to send. Stay signed in and tell a supervisor: signing out now could strand readings that only you can release.';
}

/* ── Navigation ───────────────────────────────────────────
 * Grouped by the object the work is about and ordered by how often it is opened (UI/UX audit,
 * 27 Oct 2026): GOODS IN is the daily queue, PRODUCTION what the lines recorded, SET-UP what you
 * configure so either can be judged. A page that is none of these does not belong in this shell.
 */
const GROUPS: Array<{ label: string; items: Array<{ to: string; label: string; icon: LucideIcon; count?: NavCountKey }> }> = [
  {
    label: 'Goods in',
    items: [
      { to: '/office/goods-in', label: 'Goods in', icon: PackageCheck, count: 'goodsIn' },
      { to: '/office/inspection-failures', label: 'Inspection failures', icon: TriangleAlert, count: 'failures' },
      { to: '/office/holds', label: 'Holds', icon: PauseOctagon, count: 'holds' },
    ],
  },
  {
    label: 'Production',
    items: [{ to: '/office/runs', label: 'Runs', icon: Activity }],
  },
  {
    label: 'Consumers',
    items: [{ to: '/office/complaints', label: 'Complaints', icon: MessageSquareWarning }],
  },
  {
    label: 'Set-up',
    items: [
      { to: '/office/specs', label: 'Spec master', icon: ClipboardList },
      { to: '/office/meters', label: 'Meters', icon: Gauge },
      { to: '/office/authority', label: 'Authority', icon: ShieldCheck },
      { to: '/office/competency', label: 'Competency', icon: GraduationCap },
    ],
  },
];

/** The other two areas of the app, as the operator shell names them (OperatorShell's switcher). */
const AREAS: Array<{ to: string; label: string; icon: LucideIcon }> = [
  { to: '/op/receive', label: 'Receiving', icon: Truck },
  { to: '/op', label: 'Line check', icon: ClipboardCheck },
];

/* ── Nav counts (UI/UX audit, 27 Oct 2026, item 3) ─────────
 * What needs a person, beside the register it is waiting in: lines awaiting a decision
 * (the register's own byState, the same window Goods in opens with) and open inspection
 * failures. Rule 15: a read that failed is NULL, drawn as a muted "?" that says so -
 * never 0, which would read as "nothing waiting". Loaded once per shell mount and again
 * on a route change at most every 60 s; the nav never waits on it.
 */
export type NavCountKey = 'goodsIn' | 'failures' | 'holds';
/** Per key: a number, null = could not be read, undefined = not asked yet. */
export type NavCounts = Partial<Record<NavCountKey, number | null>>;
const NAV_COUNT_LABEL: Record<NavCountKey, (n: number) => string> = {
  goodsIn: (n) => `${n} need${n === 1 ? 's' : ''} a decision`,
  failures: (n) => `${n} open failure${n === 1 ? '' : 's'}`,
  holds: (n) => `${n} hold${n === 1 ? '' : 's'} open over 14 days`,
};
const NAV_COUNT_REFRESH_MS = 60_000;

export function useNavCounts(pathname: string): NavCounts {
  const [counts, setCounts] = useState<NavCounts>({});
  const lastAt = useRef(0);
  // Unmount only, never a route change: /office redirects to /office/goods-in on the first
  // load, and cancelling on that change dropped the only answer the 60 s throttle allowed
  // (found live 27 Oct 2026: the Goods in count never appeared).
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (Date.now() - lastAt.current < NAV_COUNT_REFRESH_MS) return;
    lastAt.current = Date.now();
    const from = localDay(-14);
    const to = localDay(14);
    void api
      .get<RegisterPayload>(`/receipts?from=${from}&to=${to}`)
      .then((d) => (mounted.current ? setCounts((c) => ({ ...c, goodsIn: d?.byState?.AWAITING_DECISION ?? 0 })) : null))
      .catch(() => (mounted.current ? setCounts((c) => ({ ...c, goodsIn: null })) : null));
    void api
      .get<InspectionFailuresPayload>('/inspection-failures?status=open')
      .then((d) => (mounted.current ? setCounts((c) => ({ ...c, failures: Array.isArray(d?.failures) ? d.failures.length : null })) : null))
      .catch(() => (mounted.current ? setCounts((c) => ({ ...c, failures: null })) : null));
    // Holds open over 14 days (the register's red line). Holds switched off answers 403: no badge, not "?".
    void api
      .get<{ totals?: { over14?: number } }>('/holds?status=open')
      .then((d) => (mounted.current ? setCounts((c) => ({ ...c, holds: typeof d?.totals?.over14 === 'number' ? d.totals.over14 : null })) : null))
      .catch((err: unknown) =>
        mounted.current ? setCounts((c) => ({ ...c, holds: (err as { status?: number })?.status === 403 ? undefined : null })) : null,
      );
  }, [pathname]);
  return counts;
}

/** The badge beside a nav item. Nothing for 0 or not yet asked; "?" for a failed read. */
function NavCount({ value, label }: { value: number | null | undefined; label: (n: number) => string }) {
  if (value === undefined) return null;
  if (value === null) {
    return (
      <span
        role="img"
        aria-label="Count could not be read"
        title="Could not be read"
        className="ml-auto text-[12px] font-semibold text-(--sidebar-muted)"
      >
        ?
      </span>
    );
  }
  if (value === 0) return null;
  return (
    <span
      role="img"
      aria-label={label(value)}
      className="ml-auto inline-flex min-w-[22px] items-center justify-center rounded-(--radius-pill) bg-(--sidebar-active) px-[6px] py-[1px] text-[12px] font-semibold tabular-nums text-(--sidebar-fg)"
    >
      {value}
    </span>
  );
}

/** The nav label of the register the path is in (its detail pages included), or null. */
export function pageLabel(pathname: string): string | null {
  const items = GROUPS.flatMap((g) => g.items).sort((a, b) => b.to.length - a.to.length);
  return items.find((i) => pathname === i.to || pathname.startsWith(`${i.to}/`))?.label ?? null;
}

/* The hierarchy is the alignment: a group label and its items' icons share one
 * left edge (20px: the list's 8px inset plus the item's 12px), so each label
 * sits over its own column of icons and the item text is indented under it.
 * Before 2026-10-01 the items sat 8px LEFT of their label, which read as a flat
 * list. */
function navItemClass(isActive: boolean): string {
  return [
    'relative flex min-h-[44px] items-center gap-[12px] rounded-(--radius-sm) px-[12px] no-underline',
    'text-[14px] transition-[background-color,color] duration-150 motion-reduce:transition-none',
    // No ring class: the global outline is the ring (officeFocus); the sidebar re-points --focus.
    // A 3px bar at the sidebar's edge, never a red fill: rule 1's shape rule.
    // The bar is the accent's only appearance in the sidebar, which is what keeps
    // it an accent. The grey under it is the selected row; hover is a lighter
    // wash, so selected and hovered are never the same grey.
    "before:absolute before:left-[-8px] before:top-1/2 before:h-[20px] before:w-[3px] before:-translate-y-1/2 before:rounded-r-full before:content-['']",
    isActive
      ? 'bg-(--sidebar-active) font-semibold text-(--sidebar-fg) before:bg-brand-line'
      : 'font-medium text-(--sidebar-muted) before:bg-transparent hover:bg-white/[0.05] hover:text-(--sidebar-fg)',
  ].join(' ');
}

export function OfficeShell() {
  // No pilot banner (Amit, 27 Oct 2026: "I don't want pilot notice in app"). Pilot RECORDS still carry
  // their PilotBadge for ever (Runs, the line, Verify) - that is the mark a record needs; the window
  // itself is said in docs/pilot-runbook.md and by GET /pilot.
  const { pathname } = useLocation();
  const label = pageLabel(pathname);
  // The document title follows the register (WCAG 2.4.2), and a detail page's own record first:
  // "PO024474 · Goods in · Quality" (UI/UX audit, 31 Oct 2026, UX-20; helper in lib/pageTitle.ts).
  const pageName = usePageName();
  useDocumentTitle(pageName, label);
  const counts = useNavCounts(pathname);
  // The phone's drawer is controlled so a tap on a register closes it over the new page.
  const [navOpen, setNavOpen] = useState(false);
  // ⚠ SIGN OUT IS HAND OVER, AND OBEYS THE SAME GUARD (audit 28 Oct 2026). The two queues are
  // device-wide and stamped per account: whoever leaves with something unsent leaves it HELD for
  // themselves. Mounting them here also keeps them draining while the person is in the office.
  const { accounts } = useMsal();
  const signedInUpn = accounts.length > 0 ? currentUpn() : '';
  const queue = useQueue({ signedInUpn });
  const iir = useIirQueue(signedInUpn);
  const handOver = handOverState(queue, iir);
  return (
    <div className="flex min-h-dvh w-full bg-(--bg) text-(--fg)">
      {/* A keyboard-first shell that cannot be skipped past is a keyboard trap for
          anybody who tabs: every page begins with the same 5 nav links. */}
      <a
        href="#office-main"
        className={`sr-only focus:not-sr-only focus:absolute focus:left-(--s3) focus:top-(--s3) focus:z-30 focus:rounded-(--radius-sm) focus:bg-(--card) focus:px-(--s3) focus:py-(--s2) ${officeFocus}`}
      >
        Skip to content
      </a>

      {/* The sidebar, once, rendered two ways. ≥ md it is the persistent aside
          this shell is built around. Below md it lives in a Sheet behind a menu
          button — measured 2026-09-17: at 458px wide the fixed 232px aside left
          the register 209px, and the Coverage list wrapped one word per line.
          Desk-first is still the rule; a half-width window on a desk is a desk.

          An <aside>, not a <nav> (UI/UX audit, 31 Oct 2026, UX-46): it holds two navigations
          (Registers, Areas) plus the theme and the account, and a <nav> wrapping another <nav>
          and a Sign out button told a screen reader the whole column was one list of links. */}
      <aside
        aria-label="Quality"
        className="office-sidebar sticky top-0 hidden h-dvh w-[232px] flex-none flex-col self-start bg-(--sidebar-bg) py-(--s3) text-(--sidebar-fg) md:flex"
      >
        <OfficeNav counts={counts} handOver={handOver} />
      </aside>

      {/* THE REGISTERS ARE CHILD ROUTES DECLARED IN App.tsx, and this is where
          they render. A second <Routes> here would draw nothing: React Router
          only matches a descendant <Routes> when the parent route path ends in
          `*`, and App mounts this shell at `/office` with real children — so the
          child route would render into an <Outlet/> that does not exist while the
          descendant <Routes> matched nothing. Sidebar, blank page.

          The nav targets above must stay in step with the paths App declares. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <NewBuildNotice />
        {/* STICKY (UI/UX audit, 31 Oct 2026, UX-47): below md this bar holds the ONLY way to the
            menu, and it scrolled away with the page - on a long PO it ended 1492px above the screen,
            so changing register meant scrolling all the way back up first. */}
        <header className="sticky top-0 z-20 flex items-center gap-(--s2) border-b border-(--border) bg-(--card) px-(--s3) py-(--s2) md:hidden">
          <Sheet open={navOpen} onOpenChange={setNavOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Open navigation">
                <Menu aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="office-sidebar w-[280px] bg-(--sidebar-bg) p-0 py-(--s3) text-(--sidebar-fg)">
              <SheetTitle className="sr-only">Quality navigation</SheetTitle>
              <OfficeNav counts={counts} handOver={handOver} onNavigate={() => setNavOpen(false)} />
            </SheetContent>
          </Sheet>
          <span className="text-(length:--fs-lg) font-bold tracking-tight">Quality</span>
          {/* The compact bar carries the page, as a top app bar does; the h1 is further down. */}
          {label ? (
            <span className="truncate text-(length:--fs-sm) text-(--muted)">
              · {label}
              {pageName ? ` · ${pageName}` : ''}
            </span>
          ) : null}
        </header>
        <main id="office-main" className="min-w-0 flex-1 p-(--s4) md:p-[var(--s5)]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** The navigation itself, shared by the aside and the sheet so the two can
 *  never list different registers. */
function OfficeNav({ counts, handOver, onNavigate }: { counts: NavCounts; handOver: HandOverState; onNavigate?: () => void }) {
  const upn = currentUpn();
  const { instance } = useMsal();
  const refusal = signOutRefusal(handOver);
  // ONE SCROLL AREA, THE WHOLE COLUMN (UI/UX audit, 31 Oct 2026, UX-19). Only the register list
  // used to scroll, between a fixed header and a fixed footer (Areas, Theme, account): on a 1024x768
  // iPad window that left the list 384px, Authority cut in half and Competency out of sight, while
  // the footer kept its full height. Now the list is a plain flex-1 spacer - on a tall screen it
  // still pushes the footer to the bottom, on a short one the column scrolls as one, and Sign out's
  // refusal sentence below it is never clipped (it is reached by scrolling, like everything else).
  return (
    <div className="flex h-full flex-col overflow-y-auto">
      {/* The brand mark and the wordmark. The mark carries the red; the
          wordmark stays ink, so the sidebar holds one red thing at a time. */}
      <div className="flex items-center gap-[10px] border-b border-white/[0.06] px-[20px] pb-[18px] pt-[6px]">
        <img src="/majans-logo-mini.png" alt="" aria-hidden="true" className="h-[28px] w-[28px] rounded-[6px] object-contain" />
        <span className="font-display text-[24px] font-bold uppercase leading-none tracking-[0.04em]">Quality</span>
      </div>

      {/* "Registers" beside "Areas" (UX-46): two named navigations, each only links. */}
      <nav aria-label="Registers" className="flex-1">
        {GROUPS.map((group) => (
          <div key={group.label} className="mt-[22px] first:mt-[18px]">
            {/* A label, not a heading: the page's h1 must be the first heading a reader meets. */}
            <div className="m-0 px-[20px] pb-[6px] text-[11px] font-semibold uppercase tracking-[0.08em] text-(--sidebar-muted)">
              {group.label}
            </div>
            <ul className="m-0 flex list-none flex-col gap-[2px] px-[8px] py-0">
              {group.items.map(({ to, label, icon: Icon, count }) => (
                <li key={to}>
                  {/* aria-current="page" is NavLink's own doing. It is the only
                      thing that tells a screen reader which register is open. */}
                  <NavLink to={to} onClick={onNavigate} className={({ isActive }) => navItemClass(isActive)}>
                    <Icon aria-hidden="true" className="h-[18px] w-[18px] flex-none" strokeWidth={1.75} />
                    {label}
                    {count ? <NavCount value={counts[count]} label={NAV_COUNT_LABEL[count]} /> : null}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* THE AREAS (Amit, 27 Oct 2026): the app is three areas - Office (here), Receiving (the dock
          tablet) and Line check (the production tablet). The other two are offered in the same words
          and the same place in every shell, apart from the registers above, which are pages of this one. */}
      <nav aria-label="Areas" className="mt-[22px] border-t border-white/[0.06] px-[8px] pt-[14px]">
        <div className="m-0 px-[12px] pb-[6px] text-[11px] font-semibold uppercase tracking-[0.08em] text-(--sidebar-muted)">Switch to</div>
        <ul className="m-0 flex list-none flex-col gap-[2px] p-0">
          {AREAS.map(({ to, label, icon: Icon }) => (
            <li key={to}>
              <Link to={to} onClick={onNavigate} className={navItemClass(false)}>
                <Icon aria-hidden="true" className="h-[18px] w-[18px] flex-none" strokeWidth={1.75} />
                {label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <div className="flex flex-col gap-[14px] border-t border-white/[0.06] px-[12px] pt-[14px]">
        <ThemeSwitch />
        {/* Who every record on these pages will be attributed to (rule 2). */}
        {upn ? (
          <div className="flex items-center gap-[10px] px-[8px]">
            <span
              aria-hidden="true"
              className="flex h-[28px] w-[28px] flex-none items-center justify-center rounded-full bg-(--sidebar-active) text-[12px] font-semibold uppercase text-(--sidebar-fg)"
            >
              {upn.slice(0, 1)}
            </span>
            <span className="min-w-0">
              <span className="block text-[11px] uppercase tracking-[0.08em] text-(--sidebar-muted)">Signed in</span>
              <span className="block truncate text-[13px] text-(--sidebar-fg)" title={upn}>
                {upn}
              </span>
            </span>
          </div>
        ) : null}
        {/* A shared QA PC needs a way out beside "Signed in" (rule 2: the next record is theirs). */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="justify-start px-[8px] text-(--sidebar-muted) hover:bg-white/[0.05] hover:text-(--sidebar-fg)"
          disabled={refusal !== null}
          title={refusal ?? undefined}
          onClick={() => {
            if (refusal !== null) return;
            void instance.logoutRedirect();
          }}
        >
          {refusal === null ? 'Sign out' : handOver.depthKnown ? `Sign out (${handOver.toSend} to send or refused)` : 'Sign out (queue unreadable)'}
        </Button>
        {refusal !== null ? <p className="m-0 px-[8px] text-[12px] text-(--sidebar-muted)">{refusal}</p> : null}
      </div>
    </div>
  );
}

export default OfficeShell;
